import { Button, Icon } from '@unmind/design-system';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { OnboardingSupportOption } from '__generated__/globalTypes';
import { getSupportOptionConfig } from './supportOptions';
import { useScrollToTopOnChange } from './useScrollToTopOnChange';
import './DetailView.scss';

/** The modal points `labelledBy` at this, so the dialog has an accessible name. */
export const DETAIL_VIEW_HEADING_ID = 'welcome-modal-detail-heading';

export interface DetailViewProps {
  option: OnboardingSupportOption;
  onBack(): void;
}

/**
 * One template for every offering, driven by `supportOptions`.
 *
 * Returns content rather than a `ModalContent`: the design system matches its
 * regions by element type, so the caller composes them.
 */
export const DetailView = ({ option, onBack }: DetailViewProps) => {
  const { t: translate } = useTranslation('homepage', {
    keyPrefix: 'welcome_modal',
  });

  const rootRef = useRef<HTMLDivElement>(null);

  useScrollToTopOnChange(rootRef, option);

  const config = getSupportOptionConfig(option);

  if (config === undefined) {
    return null;
  }

  const { translationKey, detailRows } = config;

  return (
    <div className="welcome-modal-detail" ref={rootRef}>
      <div className="welcome-modal-detail__back">
        <Button
          variant="secondary"
          size="small"
          icon={{ icon: 'arrow-left', position: 'left' }}
          onClick={onBack}
        >
          {translate('details.back')}
        </Button>
      </div>

      <h2
        id={DETAIL_VIEW_HEADING_ID}
        className="title-xl welcome-modal-detail__heading"
      >
        {translate(`details.${translationKey}.title`)}
      </h2>

      <ul className="welcome-modal-detail__rows">
        {detailRows.map(({ icon, textKey }) => (
          <li key={textKey} className="welcome-modal-detail__row">
            <Icon
              className="welcome-modal-detail__row-icon"
              icon={icon}
              size="large"
              aria-hidden
            />
            <span className="body-l welcome-modal-detail__row-text">
              {translate(textKey, {
                employerName: translate('details.employer'),
              })}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export interface DetailViewCtaProps {
  option: OnboardingSupportOption;
  onCtaClick(option: OnboardingSupportOption): void;
}

/**
 * Separate from `DetailView` so it can sit in the modal's footer region, which
 * keeps it pinned outside the scrolling area on small screens.
 */
export const DetailViewCta = ({ option, onCtaClick }: DetailViewCtaProps) => {
  const { t: translate } = useTranslation('homepage', {
    keyPrefix: 'welcome_modal',
  });

  const config = getSupportOptionConfig(option);

  if (config === undefined) {
    return null;
  }

  return (
    <Button
      variant="primary"
      styling="full-width"
      onClick={() => onCtaClick(option)}
    >
      {translate(`details.${config.translationKey}.cta`)}
    </Button>
  );
};
